import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, IconButton, RadioGroup,
    FormControlLabel, Radio, TextField, Chip, CircularProgress,
} from '@mui/material';
import { MdClose, MdVisibility, MdCheckCircle, MdWarning, MdRemoveCircle } from 'react-icons/md';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { CheckInspection } from '../../services/tipos';

const AuthenticatedImage: React.FC<{ filePath: string; alt: string; style?: React.CSSProperties; onClick?: () => void }> = ({ filePath, alt, style, onClick }) => {
    const [src, setSrc] = React.useState<string>('');
    React.useEffect(() => {
        let objectUrl = '';
        const encodedPath = filePath.split('/').map(s => encodeURIComponent(s)).join('/');
        api.get(`/LocaSmartCheck/Fotos/${encodedPath}`, { responseType: 'blob' })
            .then(response => {
                objectUrl = URL.createObjectURL(response.data);
                setSrc(objectUrl);
            })
            .catch(() => {});
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [filePath]);
    if (!src) return <CircularProgress size={24} />;
    return <img src={src} alt={alt} style={style} onClick={onClick} />;
};

interface ModalAnaliseProps {
    open: boolean;
    onClose: () => void;
    inspection: CheckInspection | null;
    onSuccess: () => void;
    nomeUsuario: string;
}

function ModalAnalise({ open, onClose, inspection, onSuccess, nomeUsuario }: ModalAnaliseProps) {
    const [itemStates, setItemStates] = useState<Record<string, { status: string; notes: string }>>({});
    const [saving, setSaving] = useState(false);
    const [fotoAberta, setFotoAberta] = useState<string | null>(null);
    const [claimDone, setClaimDone] = useState(false);

    const handleOpen = async () => {
        if (!inspection || claimDone) return;
        try {
            await api.post(`/LocaSmartCheck/Vistorias/${inspection.id}/ClaimReview`);
            setClaimDone(true);
        } catch {
            // pode já estar claimed, continua
            setClaimDone(true);
        }
    };

    const getItemState = (itemId: string) => itemStates[itemId] || { status: '', notes: '' };

    const updateItem = (itemId: string, field: 'status' | 'notes', value: string) => {
        setItemStates(prev => ({
            ...prev,
            [itemId]: { ...getItemState(itemId), [field]: value },
        }));
    };

    const allItemsEvaluated = () => {
        if (!inspection?.items) return false;
        return inspection.items.every(item => {
            const state = getItemState(item.id);
            return state.status !== '';
        });
    };

    const handleFinalizar = async () => {
        if (!inspection) return;
        if (!allItemsEvaluated()) {
            toast.warning('Avalie todos os itens antes de finalizar.');
            return;
        }

        setSaving(true);
        try {
            // Completar cada item
            for (const item of inspection.items) {
                const state = getItemState(item.id);
                await api.post(`/LocaSmartCheck/Vistorias/${inspection.id}/CompletarItem`, {
                    itemId: item.id,
                    status: state.status,
                    observation: state.notes || '',
                });
            }

            // Finalizar com observação de quem analisou
            const agora = new Date();
            const dataFormatada = agora.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
            const horaFormatada = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const obsAnalise = `Analisado por ${nomeUsuario} em ${dataFormatada} às ${horaFormatada} via LocaSmartPro`;

            await api.post(`/LocaSmartCheck/Vistorias/${inspection.id}/Finalizar`, {
                notes: obsAnalise,
            });

            toast.success('Vistoria analisada com sucesso!');
            onSuccess();
            handleCloseInternal();
        } catch (error: any) {
            const msg = error.response?.data?.Mensagem || error.response?.data?.message || 'Erro ao finalizar análise';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleCloseInternal = () => {
        setItemStates({});
        setClaimDone(false);
        setFotoAberta(null);
        onClose();
    };

    if (!inspection) return null;

    return (
        <>
            <Dialog
                open={open}
                onClose={handleCloseInternal}
                maxWidth="md"
                fullWidth
                TransitionProps={{ onEntered: handleOpen }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">Analisar Vistoria</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {inspection.vehiclePlate} - {inspection.vehicleModel} | {inspection.templateName}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseInternal} size="small"><MdClose /></IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                        Avalie cada item da vistoria. Clique no ícone do olho para visualizar a foto.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {inspection.items?.map((item) => {
                            const state = getItemState(item.id);
                            const hasPhotos = item.photos && item.photos.length > 0;

                            return (
                                <Box key={item.id} sx={{
                                    p: 2, borderRadius: 2,
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: state.status === 'Ok' ? '#f1f8e9' :
                                        state.status === 'Damage' ? '#fce4ec' :
                                        state.status === 'NotApplicable' ? '#f5f5f5' : '#fff',
                                }}>
                                    {/* Header do item */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {item.templateItemName}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {hasPhotos && (
                                                <Chip
                                                    icon={<MdVisibility />}
                                                    label={`${item.photos.length} foto(s)`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    onClick={() => setFotoAberta(item.photos[0].filePath)}
                                                    sx={{ cursor: 'pointer' }}
                                                />
                                            )}
                                            {item.notes && (
                                                <Chip label={`Obs: ${item.notes}`} size="small" variant="outlined" />
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Miniaturas das fotos */}
                                    {hasPhotos && (
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                            {item.photos.map((photo) => (
                                                <Box
                                                    key={photo.id}
                                                    sx={{
                                                        width: 80, height: 60, borderRadius: 1,
                                                        overflow: 'hidden', cursor: 'pointer',
                                                        border: '2px solid #e0e0e0',
                                                        '&:hover': { border: '2px solid #1976d2' },
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        backgroundColor: '#f5f5f5',
                                                    }}
                                                >
                                                    <AuthenticatedImage
                                                        filePath={photo.filePath}
                                                        alt={photo.fileName}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onClick={() => setFotoAberta(photo.filePath)}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Status */}
                                    <RadioGroup
                                        row
                                        value={state.status}
                                        onChange={(e) => updateItem(item.id, 'status', e.target.value)}
                                    >
                                        <FormControlLabel
                                            value="Ok"
                                            control={<Radio size="small" sx={{ color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50' } }} />}
                                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <MdCheckCircle style={{ color: '#4CAF50' }} /> OK
                                            </Box>}
                                        />
                                        <FormControlLabel
                                            value="Damage"
                                            control={<Radio size="small" sx={{ color: '#f44336', '&.Mui-checked': { color: '#f44336' } }} />}
                                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <MdWarning style={{ color: '#f44336' }} /> Avaria
                                            </Box>}
                                        />
                                        <FormControlLabel
                                            value="NotApplicable"
                                            control={<Radio size="small" sx={{ color: '#9e9e9e', '&.Mui-checked': { color: '#9e9e9e' } }} />}
                                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <MdRemoveCircle style={{ color: '#9e9e9e' }} /> N/A
                                            </Box>}
                                        />
                                    </RadioGroup>

                                    {/* Observação do avaliador */}
                                    {state.status === 'Damage' && (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Descreva a avaria..."
                                            value={state.notes}
                                            onChange={(e) => updateItem(item.id, 'notes', e.target.value)}
                                            sx={{ mt: 1 }}
                                            multiline
                                            rows={2}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button variant="outlined" onClick={handleCloseInternal} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleFinalizar}
                        disabled={saving || !allItemsEvaluated()}
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <MdCheckCircle />}
                    >
                        {saving ? 'Finalizando...' : 'Finalizar Análise'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de foto ampliada */}
            <Dialog
                open={!!fotoAberta}
                onClose={() => setFotoAberta(null)}
                maxWidth="lg"
            >
                <DialogContent sx={{ p: 1, backgroundColor: '#000' }}>
                    {fotoAberta && (
                        <AuthenticatedImage
                            filePath={fotoAberta}
                            alt="Foto da vistoria"
                            style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', margin: '0 auto' }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFotoAberta(null)} variant="outlined">Fechar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ModalAnalise;
